import React, { useState } from 'react';
import { usePharmacyPickerContext } from './PharmacyPickerContext';
import { IoIosPaperPlane } from 'react-icons/io';
import { FaClipboardList } from 'react-icons/fa';
import { IoCheckmarkCircle } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import * as translator from '../../utils/translator';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { useCustomer } from '../../customHooks/useCustomer';

interface Pharmacy {
  stockCode: string;
  name: string;
  address: string;
  location?: { lat: number; lng: number };
  is_external_pharmacy: boolean;
  opening_hours?: string;
  external_integration_code?: string;
  distance: number;          // in km
  productsCount?: number;
}

interface PharmacyListProps {
  pharmacies: Pharmacy[];
  selectedStockCode?: string | null;
  onPharmacySelect: (stockCode: string) => void;
  onShowProducts?: (stockCode: string) => void;
  onPharmacyHighlight?: (stockCode: string | null) => void;
}

export const PharmacyList: React.FC<PharmacyListProps> = ({
  pharmacies,
  selectedStockCode,
  onPharmacySelect,
  onShowProducts,
  onPharmacyHighlight,
}) => {
  const { language } = useParams<{ language: string }>();
  const { customerState } = useCustomer();
  const { selectedService, getProductPriceForStock: getPriceForStock } = usePharmacyPickerContext();
  const [expandedStockCode, setExpandedStockCode] = useState<string | null>(null);

  // ✅ sort by distance and take only the 5 closest
  const visiblePharmacies = [...pharmacies]
    .sort((a, b) => a.distance - b.distance)
    .slice(0, 5);

  const handleCardClick = (stockCode: string) => {
    const pharmacy = pharmacies.find(p => p.stockCode === stockCode);
    if (pharmacy) {
      const userId = customerState?.customerID || null;
      
      // Get price from selected product's available stocks
      const productsAvailablePrice = getPriceForStock?.(stockCode);
      
      // Track pharmacy card click
      MainTracker.track("custom", "pharmacy_card_click", {
        user_id: userId,
        pharmacy_id: pharmacy.stockCode,
        pharmacy_name: pharmacy.name,
        distance_km: pharmacy.distance || null,
        products_available_Price: productsAvailablePrice,
      });
    }
    
    const isCurrentlyExpanded = expandedStockCode === stockCode;
    setExpandedStockCode((prev) => (prev === stockCode ? null : stockCode));
    
    // Highlight pharmacy on map when expanding, clear when collapsing
    if (onPharmacyHighlight) {
      onPharmacyHighlight(isCurrentlyExpanded ? null : stockCode);
    }
  };


  const handleShowProducts = (e: React.MouseEvent, stockCode: string) => {
    e.stopPropagation();
    // Select the pharmacy to open the modal on the map
    onPharmacySelect(stockCode);
    if (onShowProducts) onShowProducts(stockCode);
    // Clear highlight when selecting
    if (onPharmacyHighlight) {
      onPharmacyHighlight(null);
    }
  };


  if (visiblePharmacies.length === 0) return null;

  return (
    <div className="w-full flex flex-col gap-2">
      {visiblePharmacies.map((pharmacy) => {
        const isSelected = selectedStockCode === pharmacy.stockCode;
        const isExpanded = expandedStockCode === pharmacy.stockCode;
        
        // Determine which badge to show - only in prescription_only service
        const isOnlyPrescriptionService = selectedService === "prescription_only";
        const showUnknownStockBadge = isOnlyPrescriptionService && pharmacy.is_external_pharmacy && !pharmacy.external_integration_code;
        const showPayOnceBadge = isOnlyPrescriptionService && !pharmacy.is_external_pharmacy;

        return (
          <div
            key={pharmacy.stockCode}
            onClick={() => handleCardClick(pharmacy.stockCode)}
            className={`
              w-full
              rounded-lg
              border border-solid
              px-4 py-3
              transition-all duration-200
              ${isSelected || isExpanded
                ? 'bg-[#F7FAEF] border-[#0A9281] scale-105' 
                : 'bg-white border-gray-300 cursor-pointer'
              }
            `}
          >
            <div className="flex items-start">

              {/* center: text info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#00302E] mb-1">
                  {pharmacy.name}
                </p>

                <p className="text-xs text-[#666666] mb-1">
                  {pharmacy.address}
                </p>

                {/* Badges */}
                {showPayOnceBadge && (
                  <div className="inline-flex items-center gap-1 mb-1 px-2 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: '#037D73', color: '#FFFFFF' }}>
                    <IoCheckmarkCircle className="w-4 h-4" />
                    <span>{translator.translate(language, 'choose_treatment', 'shipping_and_service_selection/pay_once')}</span>
                  </div>
                )}

                <p className="text-xs text-[#666666] flex items-center gap-1.5">
                  <IoIosPaperPlane className="w-4 h-4" />
                  {pharmacy.distance.toFixed(1).replace('.', ',')} {translator.translate(language, 'choose_treatment', 'default/km_from_you')}
                </p>

                {isExpanded && pharmacy.opening_hours && (
                  <p className="mt-2 text-xs text-[#8BA0B8]">
                    {pharmacy.opening_hours.replace(/null-null/g, 'Geschlossen')}
                  </p>
                )}
              </div>

              {/* right: badge or products count, + button */}
              <div className="flex flex-col items-end flex-shrink-0 ml-3 gap-2">
                {showUnknownStockBadge ? (
                  <>
                    <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: '#FF9800', color: '#FFFFFF' }}>
                      <FaClipboardList className="w-4 h-4" />
                      <span>{translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')}</span>
                    </div>
                    {isExpanded && onShowProducts && (
                      <button
                        type="button"
                        onClick={(e) => handleShowProducts(e, pharmacy.stockCode)}
                        className="bg-[#06D1A0] text-white text-xs font-semibold px-4 py-2 rounded-md border-none shadow-sm cursor-pointer"
                      >
                        {translator.translate(language, 'choose_treatment', 'default/show_products')}
                      </button>
                    )}
                  </>
                ) : (
                  <>
                    {pharmacy.productsCount !== undefined && (
                      <div className="text-right leading-tight">
                        <p className="text-[11px] text-[#949494]">
                          {pharmacy.productsCount} {translator.translate(language, 'choose_treatment', 'default/products')}
                        </p>
                        <p className="text-[11px] text-[#949494]">{translator.translate(language, 'choose_treatment', 'default/available')}</p>
                      </div>
                    )}
                    {isExpanded && onShowProducts && (
                      <button
                        type="button"
                        onClick={(e) => handleShowProducts(e, pharmacy.stockCode)}
                        className="mt-auto bg-[#06D1A0] text-white text-xs font-semibold px-4 py-2 rounded-md border-none shadow-sm cursor-pointer"
                      >
                        {translator.translate(language, 'choose_treatment', 'default/show_products')}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
